import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { createPortal } from "react-dom";

interface DropdownMenuProps {
  trigger: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  title?: string;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  position?: "left" | "right";
  size?: "sm" | "md";
  placement?: "auto" | "top" | "bottom";
}

const CustomDropdown = ({
  trigger,
  children,
  className,
  title,
  isOpen,
  setIsOpen,
  position = "right",
  size = "md",
  placement = "auto",
}: DropdownMenuProps) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top?: number;
    bottom?: number;
    right?: number;
    left?: number;
    placement: "top" | "bottom";
  }>({
    top: 0,
    bottom: 0,
    right: 0,
    left: 0,
    placement: "bottom",
  });

  const updatePosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const dropdownHeight =
        dropdownRef.current?.getBoundingClientRect().height || 300; // Estimate if not yet rendered
      const windowHeight = window.innerHeight;
      const spaceBelow = windowHeight - rect.bottom;
      const spaceAbove = rect.top;

      // Determine if dropdown should appear above or below
      let finalPlacement: "top" | "bottom" = "bottom";
      if (placement === "top") {
        finalPlacement = "top";
      } else if (placement === "bottom") {
        finalPlacement = "bottom";
      } else {
        // Auto placement
        if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
          finalPlacement = "top";
        }
      }

      setDropdownPosition({
        top:
          finalPlacement === "bottom"
            ? rect.bottom + window.scrollY + 8
            : undefined,
        bottom:
          finalPlacement === "top" ? windowHeight - rect.top + 8 : undefined,
        right:
          position === "right"
            ? window.innerWidth - rect.right - window.scrollX
            : undefined,
        left: position === "left" ? rect.left + window.scrollX : undefined,
        placement: finalPlacement,
      });
    }
  }, [position, placement]);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      window.addEventListener("scroll", updatePosition);
      window.addEventListener("resize", updatePosition);
    }

    return () => {
      window.removeEventListener("scroll", updatePosition);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, updatePosition]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        closeDropdown();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeDropdown();
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, closeDropdown]);

  return (
    <>
      <div
        ref={triggerRef}
        onClick={() => setIsOpen(!isOpen)}
        className="cursor-pointer"
      >
        {trigger}
      </div>

      <AnimatePresence>
        {isOpen && (
          <>
            {createPortal(
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 bg-black/15"
                style={{ zIndex: 40 }}
                onClick={closeDropdown}
              />,
              document.body
            )}

            {createPortal(
              <div
                ref={dropdownRef}
                style={{ position: "absolute", zIndex: 50 }}
              >
                <motion.div
                  initial={{
                    opacity: 0,
                    y: dropdownPosition.placement === "bottom" ? -5 : 5,
                  }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{
                    opacity: 0,
                    y: dropdownPosition.placement === "bottom" ? -5 : 5,
                  }}
                  transition={{ duration: 0.2, ease: "easeInOut" }}
                  style={{
                    position: "fixed",
                    top: dropdownPosition.top,
                    bottom: dropdownPosition.bottom,
                    right: dropdownPosition.right,
                    left: dropdownPosition.left,
                  }}
                  className="min-w-[8rem] overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-black/5 focus:outline-none"
                  onClick={(e) => e.stopPropagation()}
                >
                  {title && (
                    <h2
                      className={`py-3 px-4 text-base tracking-wide font-medium text-neutral-700 border-b border-neutral-200 ${
                        size === "sm" ? "text-sm" : "text-base"
                      }`}
                    >
                      {title}
                    </h2>
                  )}
                  <div className={className}>{children}</div>
                </motion.div>
              </div>,
              document.body
            )}
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default CustomDropdown;
